import {
  MODE_PREVIEW,
  MODE_VIEW_AS,
  VIEW_AS_EMPLOYEES_RETURN_PATH,
  applyImpersonationAccountContext,
  getViewAsLandingPath,
  stashAdminRestoreContext,
} from "@components/utils/impersonationSession";
import {
  getEmployeeAccessId,
  getEmployeeDisplayName,
  getViewAsTargetStatus,
} from "@components/utils/viewAsEligibility";

/**
 * Start + exchange view-as/preview in the account app (same tab), then hard-navigate.
 *
 * @param {object} options
 * @param {"view_as"|"preview"} options.mode
 * @param {object} options.employee
 * @param {string} [options.targetStatus] — override for banner/status flag
 * @param {(args: {accessId?: number, employeeId?: number}) => Promise<{data: {exchange_code: string}}>} options.startApi
 * @param {(code: string) => Promise<{data: object}>} options.exchangeApi
 */
export async function startAndEnterViewAsOrPreview({
  mode,
  employee,
  targetStatus,
  startApi,
  exchangeApi,
}) {
  const accessId = getEmployeeAccessId(employee);
  const employeeId = employee?.id;

  if (mode === MODE_VIEW_AS && !accessId) {
    throw new Error("Missing access id for Impersonate.");
  }
  if (mode === MODE_PREVIEW && !employeeId) {
    throw new Error("Missing employee id for Preview.");
  }

  const startPayload =
    mode === MODE_PREVIEW ? { employeeId } : { accessId };
  const { data: startData } = await startApi(startPayload);
  const exchangeCode = startData?.exchange_code;
  if (!exchangeCode) {
    throw new Error("No exchange code returned.");
  }

  stashAdminRestoreContext();

  const { data } = await exchangeApi(exchangeCode);
  const resolvedStatus = targetStatus || getViewAsTargetStatus(employee);
  // Prefer the caller mode so Exit/banner never fall through to act-as behavior
  // if the exchange payload omits or mis-labels `mode`.
  const resolvedMode = mode || data.mode;

  applyImpersonationAccountContext({
    ...data,
    mode: resolvedMode,
    read_only: true,
    targetStatus: resolvedStatus,
    returnUrl: VIEW_AS_EMPLOYEES_RETURN_PATH,
  });

  const landing = getViewAsLandingPath(resolvedMode, data.access_permission);
  window.location.assign(landing);
}

export function buildViewAsConfirmMessage(employee, targetStatus) {
  const name = getEmployeeDisplayName(employee);
  const statusLabel = targetStatus
    ? targetStatus.charAt(0).toUpperCase() + targetStatus.slice(1)
    : "Unavailable";
  return `${name}'s access is ${statusLabel}. You can still start a read-only Impersonate session. Continue?`;
}
